import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Authenticator from 'components/Authenticator';
import CancelButton from 'components/buttons/Cancel';
import ConfirmButton from 'components/buttons/Confirm';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardText from 'components/Card/Text';
import Dialog from 'components/Dialog';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import auth from 'lib/auth';
import store from 'lib/store';
import { useQuery } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { GET_PROFILE_INFO } from './schema/apolloQueries/commonQueries';
import { useAudienceTrackerContext } from './lib/trackers/Audience';

export const AuthContext = createContext(null);
export const AuthProvider = ({ children }) => {
  const { setUserId, deviceRegistered } = useAudienceTrackerContext();
  const [birthdate, setBirthdate] = useState(null);
  const [signInDialog, setSignInDialog] = useState(false);
  const [signOutDialog, setSignOutDialog] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showLoyaltyMessage, setShowLoyaltyMessage] = useState(false);

  const hideSignInDialog = useCallback(() => setSignInDialog(false), []);
  const hideSignOutDialog = useCallback(() => setSignOutDialog(false), []);

  const showSignInDialog = useCallback(() => {
    const {
      location: { pathname, origin, search },
    } = window;
    const p = pathname !== '/' ? pathname : '/discover/events';
    window.location.href = `${process.env.REACT_APP_RIPPLE_LOGIN}?continue=${encodeURIComponent(`${origin}${p}${search ? encodeURIComponent(search) : ''}`)}&clientId=${process.env.WEB_CLIENT_ID}&deviceId=${localStorage?.getItem('rs_device_id')}`;
  }, []);

  const showSignOutDialog = useCallback(() => setSignOutDialog(true), []);

  const handleIntercepted = useCallback(async (action, fn) => {
    const intercepted = await store.get('intercepted');
    if (intercepted && intercepted.action === action) {
      await store.delete('intercepted');
      fn(intercepted.params);
    }
  }, []);

  const interceptAction = useCallback(
    async (action, params) => {
      await store.save('intercepted', { action, params });
      showSignInDialog();
    },
    [showSignInDialog],
  );

  const { loading, data } = useQuery(GET_PROFILE_INFO);
  loadDevMessages();
  loadErrorMessages();

  useEffect(() => {
    setUserId(data?.getProfile?.personId || null);
  }, [data, loading]);

  return (
    <AuthContext.Provider
      value={{
        birthdate,
        handleIntercepted,
        hideSignInDialog,
        hideSignOutDialog,
        interceptAction,
        setBirthdate,
        showSignInDialog,
        showSignOutDialog,
        showLoyaltyMessage,
        setShowLoyaltyMessage,
      }}
    >
      {deviceRegistered && children}
      <Dialog onRequestClose={hideSignInDialog} open={signInDialog}>
        <Authenticator
          showLoyaltyMessage={showLoyaltyMessage}
          setShowLoyaltyMessage={setShowLoyaltyMessage}
          onAuthenticated={useCallback(() => {
            if (window) {
              const redirect = auth.getRedirect();
              if (redirect === window.location.pathname) {
                window.location.reload();
              } else {
                window.location.replace(redirect);
              }
            }
          }, [])}
        />
      </Dialog>
      <Dialog fullWidth onRequestClose={hideSignOutDialog} open={signOutDialog}>
        <Card>
          <LoadBlur loading={signingOut}>
            <CardText>Are you sure you want to sign out?</CardText>
            <CardActions>
              <CancelButton secondary onClick={hideSignOutDialog} />
              <RowSpacer />
              <ConfirmButton
                label='Sign Out'
                primary
                onClick={useCallback(async () => {
                  setSigningOut(true);
                  await store.save('skipUntilNextLogin', false);
                  await auth.logout();
                }, [])}
              />
            </CardActions>
          </LoadBlur>
        </Card>
      </Dialog>
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * @returns {{
 *  handleIntercepted: function(action: string, callback: function),
 *  hideSignInDialog: function,
 *  hideSignOutDialog: function,
 *  interceptAction: function(action: string, params: object),
 *  requireAuth: function(fn: function),
 *  showSignInDialog: function,
 *  showSignOutDialog: function,
 *  }}
 */
export const useAuthContext = () => useContext(AuthContext);
